import { useEffect, useState } from "react";
import { BellRing, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Panel, Spinner } from "@/components/common";
import { Switch } from "@/components/ui/switch";

const DEFAULTS = { enabled: true, time: "20:00", all_clear: false };

const MorningLeads = () => {
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/morning-leads/settings").then((r) => setM(r.data)).catch(() => setM(null)); }, []);
  const toggle = async (enabled) => {
    try { const { data } = await api.put("/morning-leads/settings", { enabled }); setM(data); toast.success(enabled ? "Morning email ON" : "Morning email OFF"); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/morning-leads/test"); toast.success(data.sent ? `TEST morning email sent (${data.count} leads)` : data.note || data.error); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!m) return null;
  const times = m.days.map((d, i) => `${d} ${m.opening[i] || "closed"}`).join(" · ");
  return (
    <div className="mt-5 border-t border-[#1e293b] pt-4" data-testid="morning-leads-settings">
      <div className="flex flex-wrap items-center gap-6 text-sm text-slate-300">
        <label className="flex items-center gap-2"><Switch data-testid="morning-enabled-switch" checked={m.enabled} onCheckedChange={toggle} /> Morning email at opening: New leads that arrived while closed</label>
        <button data-testid="morning-send-test-btn" onClick={test} disabled={busy} className="cc-btn cc-btn-ghost px-3 py-1.5 text-xs">{busy ? <Spinner className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />} Send test morning email</button>
      </div>
      <div className="mt-2 text-xs text-slate-500" data-testid="morning-times">Sent only when there are such leads, at {times}. Same one-tap buttons (confirm tap, single use, 7-day expiry).</div>
    </div>
  );
};

export default function LeadNudges() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState("");
  useEffect(() => { api.get("/nudges/settings").then((r) => { setS(r.data); setSaved(r.data.time); }).catch(() => setS({ ...DEFAULTS, error: true })); }, []);
  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    try { const { data } = await api.put("/nudges/settings", { enabled: next.enabled, time: next.time, all_clear: next.all_clear }); setS(data); toast.success("Lead nudge settings saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/nudges/test"); toast.success(`TEST nudge sent (${data.count} open lead${data.count === 1 ? "" : "s"})`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return <Spinner />;
  return (
    <Panel title="Evening lead nudge" icon={BellRing} testid="lead-nudges-panel">
      <p className="mb-4 text-sm text-slate-400">Every evening, an email lists New and Contacted leads with one-tap buttons (Contacted, Booked, Not a fit, Spam). Buttons work once, expire after 7 days and need no login. Booked uploads the ad click to Google Ads when the lead has a real click ID.</p>
      <div className="flex flex-wrap items-center gap-6 text-sm text-slate-300">
        <label className="flex items-center gap-2"><Switch data-testid="nudge-enabled-switch" checked={s.enabled} onCheckedChange={(v) => save({ enabled: v })} /> Send daily</label>
        <label className="flex items-center gap-2">Send at <input data-testid="nudge-time-input" type="time" value={s.time} onChange={(e) => setS({ ...s, time: e.target.value })} onBlur={(e) => e.target.value !== saved && (setSaved(e.target.value), save({ time: e.target.value }))} className="cc-input w-28 px-2 py-1" /> SGT</label>
        <label className="flex items-center gap-2"><Switch data-testid="nudge-allclear-switch" checked={s.all_clear} onCheckedChange={(v) => save({ all_clear: v })} /> Send "All leads updated ✓" when nothing is open</label>
        <button data-testid="nudge-send-test-btn" onClick={test} disabled={busy} className="cc-btn cc-btn-ghost px-3 py-1.5 text-xs">{busy ? <Spinner className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />} Send test nudge</button>
      </div>
      {s.last_result && <div className="mt-3 text-xs text-slate-500" data-testid="nudge-last-result">Last run {fmtDate(s.last_result.at)}: {s.last_result.sent ? `sent (${s.last_result.count} leads)` : s.last_result.note || s.last_result.error}</div>}
      <MorningLeads />
    </Panel>
  );
}
